"use client";

import { createContext, useContext, useMemo, type ReactNode } from "react";
import { DEFAULT_LOCALE, pickTranslations, type Locale } from "./index";

/**
 * Locale context — Client Components consume the active locale via `useLocale()`.
 *
 * Server Components don't need this — they call `detectLocale()` directly from
 * `lib/i18n/detect.ts` and pass translations down as props.
 *
 * For Client Components, the parent Server Component should:
 *   1. Detect locale (`detectLocale()`)
 *   2. Wrap the tree in `<LocaleProvider locale={...}>`
 *   3. Children call `useLocale()` to read it
 */

const LocaleContext = createContext<Locale>(DEFAULT_LOCALE);

export function LocaleProvider({
  locale,
  children,
}: {
  locale: Locale;
  children: ReactNode;
}) {
  return <LocaleContext.Provider value={locale}>{children}</LocaleContext.Provider>;
}

/** Returns the active locale (defaults to `DEFAULT_LOCALE` if no provider). */
export function useLocale(): Locale {
  return useContext(LocaleContext);
}

/**
 * Returns scoped translations object for the active locale.
 *
 * @example
 *   import { setupTranslations } from "@/app/setup/i18n";
 *
 *   function MyComponent() {
 *     const t = useT(setupTranslations);
 *     return <h1>{t.step0.title}</h1>;
 *   }
 *
 * Translations objects must be `Record<Locale, T>` — `useT` picks the active
 * locale's slice. Type inference flows from the input — `t.step0.title` is
 * fully typed (autocomplete + compile-time validation).
 */
export function useT<T>(translations: Record<Locale, T>): T {
  const locale = useLocale();
  return useMemo(() => pickTranslations(translations, locale), [translations, locale]);
}
